import { getSubjectLabels } from '@/lib/settings';
import React, { useCallback, useEffect, useState } from 'react';
import { ImageIcon, Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../../../lib/api/core';
import { cmsTeamApi, type CmsTeamMember, type CmsTeamMemberInput } from '../../../../lib/api/cms';
import { Button } from '../../../../components/ui/button';
import { Input } from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';
import { Switch } from '../../../../components/ui/switch';
import { Label } from '../../../../components/ui/label';
import { Card, CardContent } from '../../../../components/ui/card';
import LoeschenMitGrundDialog from '../../../../components/ui/loeschen-mit-grund-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../../components/ui/dialog';
import { MediaPicker } from '../../../../components/cms/MediaPicker';

interface StandortItem {
  id: number;
  name: string;
}

interface FachItem {
  id: number;
  name: string;
}

const EMPTY_DRAFT: CmsTeamMemberInput = {
  name: '',
  aufgaben: '',
  beschreibung: '',
  portrait_url: '',
  portrait_alt: '',
  is_active: true,
  standort_ids: [],
  fach_ids: [],
  sort_order: 0,
};

const CmsTeamMembersPage: React.FC = () => {
  const [members, setMembers] = useState<CmsTeamMember[]>([]);
  const [standorte, setStandorte] = useState<StandortItem[]>([]);
  const [faecher, setFaecher] = useState<FachItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<CmsTeamMember | null>(null);
  const [draft, setDraft] = useState<CmsTeamMemberInput>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [zuLoeschen, setZuLoeschen] = useState<CmsTeamMember | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s, f] = await Promise.all([
        cmsTeamApi.list(),
        api.get<StandortItem[]>('/api/standorte?active_only=false'),
        api.get<FachItem[]>('/api/faecher?active_only=false'),
      ]);
      setMembers(m);
      setStandorte(Array.isArray(s) ? s : []);
      setFaecher(Array.isArray(f) ? f : []);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Team konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setOpen(true);
  };

  const openEdit = (m: CmsTeamMember) => {
    setEditing(m);
    setDraft({
      name: m.name,
      aufgaben: m.aufgaben ?? '',
      beschreibung: m.beschreibung ?? '',
      portrait_url: m.portrait_url ?? '',
      portrait_alt: m.portrait_alt ?? '',
      is_active: m.is_active,
      standort_ids: m.standort_ids,
      fach_ids: m.fach_ids,
      sort_order: m.sort_order,
    });
    setOpen(true);
  };

  const set = (key: keyof CmsTeamMemberInput, value: unknown) =>
    setDraft((d) => ({ ...d, [key]: value }));

  const toggleStandort = (id: number) =>
    setDraft((d) => {
      const ids = d.standort_ids || [];
      return { ...d, standort_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] };
    });

  const toggleFach = (id: number) =>
    setDraft((d) => {
      const ids = d.fach_ids || [];
      return { ...d, fach_ids: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] };
    });

  const save = async () => {
    if (!draft.name.trim()) {
      toast.error('Name ist erforderlich');
      return;
    }
    setSaving(true);
    try {
      if (editing) await cmsTeamApi.update(editing.id, draft);
      else await cmsTeamApi.create(draft);
      setOpen(false);
      await load();
      toast.success(editing ? 'Teammitglied gespeichert' : 'Teammitglied erstellt');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const quickToggle = async (m: CmsTeamMember, value: boolean) => {
    setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_active: value } : x)));
    try {
      await cmsTeamApi.update(m.id, { is_active: value });
    } catch (e) {
      setMembers((prev) => prev.map((x) => (x.id === m.id ? { ...x, is_active: !value } : x)));
      toast.error(e instanceof Error ? e.message : 'Änderung fehlgeschlagen');
    }
  };

  const remove = (m: CmsTeamMember) => setZuLoeschen(m);

  const loeschen = async (m: CmsTeamMember, grund: string) => {
    await cmsTeamApi.remove(m.id, grund);
    setMembers((prev) => prev.filter((x) => x.id !== m.id));
    setZuLoeschen(null);
    toast.success('Teammitglied gelöscht');
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Team</h1>
          <p className="text-sm text-muted-foreground">
            Teammitglieder zentral verwalten und Standorten zuordnen.
          </p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> Neues Teammitglied
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : members.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Noch keine Teammitglieder. Lege das erste an.
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y p-0">
            {members.map((m) => (
              <div key={m.id} className="flex flex-wrap items-center gap-4 px-4 py-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-full border bg-muted">
                  {m.portrait_url ? (
                    <img src={m.portrait_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <ImageIcon className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate font-medium">{m.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {m.aufgaben ? `${m.aufgaben} · ` : ''}
                    {m.standort_ids.length === 0
                      ? 'Alle Standorte'
                      : `${m.standort_ids.length} Standort(e)`}
                    {(m.fach_ids?.length ?? 0) > 0 ? ` · ${m.fach_ids.length} ${getSubjectLabels().singular}/${getSubjectLabels().plural}` : ''}
                  </div>
                </div>
                <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Switch
                    checked={m.is_active}
                    onCheckedChange={(v: boolean) => quickToggle(m, v)}
                  />
                  Aktiv
                </label>
                <Button type="button" variant="outline" size="sm" onClick={() => openEdit(m)}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => remove(m)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="xl" className="max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Teammitglied: ${editing.name}` : 'Neues Teammitglied'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input value={draft.name} onChange={(e) => set('name', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Portrait</Label>
              <div className="flex items-center gap-3">
                <div className="flex h-16 w-16 items-center justify-center overflow-hidden rounded-full border bg-muted">
                  {draft.portrait_url ? (
                    <img src={draft.portrait_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <ImageIcon className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <Button type="button" variant="outline" size="sm" onClick={() => setPickerOpen(true)}>
                  Aus Bibliothek
                </Button>
                {draft.portrait_url && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => set('portrait_url', '')}>
                    Entfernen
                  </Button>
                )}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Portrait Alt-Text</Label>
              <Input value={draft.portrait_alt ?? ''} onChange={(e) => set('portrait_alt', e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Aufgaben</Label>
              <Input value={draft.aufgaben ?? ''} onChange={(e) => set('aufgaben', e.target.value)} />
              <p className="text-xs text-muted-foreground">
                Mehrere Rollen mit Komma trennen – werden als Badges angezeigt.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Beschreibung</Label>
              <Textarea value={draft.beschreibung ?? ''} onChange={(e) => set('beschreibung', e.target.value)} rows={4} />
            </div>
            <div className="space-y-2">
              <Label>Standorte (leer = alle)</Label>
              <div className="flex flex-wrap gap-2">
                {standorte.map((s) => {
                  const active = (draft.standort_ids || []).includes(s.id);
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => toggleStandort(s.id)}
                      className={`rounded-full border px-3 py-1 text-sm ${
                        active ? 'border-primary bg-primary/10 text-primary' : 'border-input text-muted-foreground'
                      }`}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label>{getSubjectLabels().plural}</Label>
              <p className="text-xs text-muted-foreground">
                {getSubjectLabels().plural}, die diese Person unterrichtet. Steuert, auf welchen „Standort × {getSubjectLabels().singular}“-Seiten
                sie als Coach zur Auswahl steht.
              </p>
              <div className="flex flex-wrap gap-2">
                {faecher.map((f) => {
                  const active = (draft.fach_ids || []).includes(f.id);
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => toggleFach(f.id)}
                      className={`rounded-full border px-3 py-1 text-sm ${
                        active ? 'border-primary bg-primary/10 text-primary' : 'border-input text-muted-foreground'
                      }`}
                    >
                      {f.name}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Switch checked={draft.is_active !== false} onCheckedChange={(v: boolean) => set('is_active', v)} />
                Aktiv
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Abbrechen
            </Button>
            <Button type="button" loading={saving} onClick={save}>
              Speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => set('portrait_url', url)}
      />
      <LoeschenMitGrundDialog
        offen={zuLoeschen !== null}
        titel={`Teammitglied „${zuLoeschen?.name ?? ''}“ löschen?`}
        onAbbrechen={() => setZuLoeschen(null)}
        onLoeschen={async (grund) => {
          if (zuLoeschen) await loeschen(zuLoeschen, grund);
        }}
      />
    </div>
  );
};

export default CmsTeamMembersPage;
